import {
    View,
    Text,
    StyleSheet,
    Modal,
    TouchableOpacity,
} from 'react-native';
import {Image} from 'expo-image';
import {useTranslation} from 'react-i18next';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {Check} from 'lucide-react-native';
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {ThemeType} from "@/theming/baseTheme";
import {AppDataOrganization} from "@/data/app-data-interfaces";

interface OrganizationSwitcherSheetProps {
    visible: boolean;
    organizations: { id: string; organization: AppDataOrganization }[];
    currentOrgId: string;
    onSelectOrg: (orgId: string) => void;
    onClose: () => void;
}

export const OrganizationSwitcherSheet = ({
    visible,
    organizations,
    currentOrgId,
    onSelectOrg,
    onClose,
}: OrganizationSwitcherSheetProps) => {
    const {t} = useTranslation();
    const {theme} = useCurrentTheme();
    const insets = useSafeAreaInsets();
    const styles = createStyles(theme, insets);

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    return (
        <Modal
            visible={visible}
            transparent={true}
            animationType="slide"
            onRequestClose={onClose}
        >
            <TouchableOpacity
                style={styles.modalOverlay}
                activeOpacity={1}
                onPress={onClose}
            >
                <TouchableOpacity activeOpacity={1}>
                    <View style={styles.modalContent}>
                        <View style={styles.modalHeader}>
                            <Text style={styles.modalTitle}>{t('orgSelection.switchOrg')}</Text>
                            <Text style={styles.modalSubtitle}>{t('orgSelection.switchOrgSubtitle')}</Text>
                        </View>

                        <View style={styles.orgList}>
                            {organizations.map(({id, organization}) => {
                                const isCurrent = id === currentOrgId;
                                return (
                                    <TouchableOpacity
                                        key={id}
                                        style={[styles.orgOption, isCurrent && styles.orgOptionCurrent]}
                                        onPress={() => onSelectOrg(id)}
                                    >
                                        <View style={styles.orgContent}>
                                            <View style={styles.orgIcon}>
                                                {organization.icon ? (
                                                    <Image
                                                        source={{uri: organization.icon}}
                                                        placeholder={{blurhash: organization.iconBlurhash}}
                                                        style={styles.orgIconImage}
                                                        contentFit="contain"
                                                        cachePolicy="memory-disk"
                                                    />
                                                ) : (
                                                    <Text style={styles.orgInitials}>
                                                        {getInitials(organization.name)}
                                                    </Text>
                                                )}
                                            </View>
                                            <View style={styles.orgDetails}>
                                                <Text style={styles.orgName} numberOfLines={1}>
                                                    {organization.name}
                                                </Text>
                                                {isCurrent && (
                                                    <Text style={styles.currentLabel}>
                                                        {t('orgSelection.currentOrg')}
                                                    </Text>
                                                )}
                                                {!isCurrent && organization.subtitle && (
                                                    <Text style={styles.orgSubtitle} numberOfLines={1}>
                                                        {organization.subtitle}
                                                    </Text>
                                                )}
                                            </View>
                                            {isCurrent && (
                                                <Check size={20} color={theme.colors.primary}/>
                                            )}
                                        </View>
                                    </TouchableOpacity>
                                );
                            })}
                        </View>

                        <TouchableOpacity
                            style={styles.cancelButton}
                            onPress={onClose}
                        >
                            <Text style={styles.cancelButtonText}>{t('common.cancel')}</Text>
                        </TouchableOpacity>
                    </View>
                </TouchableOpacity>
            </TouchableOpacity>
        </Modal>
    );
};

const createStyles = (theme: ThemeType, insets: { bottom: number }) =>
    StyleSheet.create({
        modalOverlay: {
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            justifyContent: 'flex-end',
        },
        modalContent: {
            backgroundColor: theme.colors.background,
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            paddingTop: 12,
            paddingBottom: insets.bottom + 20,
        },
        modalHeader: {
            paddingHorizontal: 24,
            paddingVertical: 20,
            borderBottomWidth: 1,
            borderBottomColor: theme.colors.border,
            marginBottom: 8,
        },
        modalTitle: {
            fontSize: 22,
            fontWeight: 'bold',
            color: theme.colors.text,
            marginBottom: 4,
        },
        modalSubtitle: {
            fontSize: 14,
            color: theme.colors.textSecondary,
        },
        orgList: {
            paddingHorizontal: 20,
            paddingVertical: 8,
        },
        orgOption: {
            backgroundColor: theme.colors.surface,
            borderRadius: 12,
            marginBottom: 12,
            borderWidth: 1,
            borderColor: theme.colors.border,
            overflow: 'hidden',
        },
        orgOptionCurrent: {
            borderColor: theme.colors.primary,
        },
        orgContent: {
            flexDirection: 'row',
            alignItems: 'center',
            padding: 16,
        },
        orgIcon: {
            width: 48,
            height: 48,
            borderRadius: 8,
            backgroundColor: `${theme.colors.border}80`,
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 16,
            overflow: 'hidden',
        },
        orgIconImage: {
            width: '100%',
            height: '100%',
        },
        orgInitials: {
            fontSize: theme.fontSize.lg,
            fontWeight: 'bold',
            color: theme.colors.text,
        },
        orgDetails: {
            flex: 1,
        },
        orgName: {
            fontSize: 16,
            color: theme.colors.text,
            fontWeight: '600',
            marginBottom: 2,
        },
        currentLabel: {
            fontSize: 13,
            color: theme.colors.primary,
            fontWeight: '500',
        },
        orgSubtitle: {
            fontSize: 13,
            color: theme.colors.textSecondary,
        },
        cancelButton: {
            padding: 20,
            marginTop: 8,
            borderTopWidth: 1,
            borderTopColor: theme.colors.border,
        },
        cancelButtonText: {
            fontSize: 16,
            color: theme.colors.textSecondary,
            textAlign: 'center',
            fontWeight: '500',
        },
    });
